'use client';

import { c as _c } from "react/compiler-runtime";
import clsx from 'clsx';
import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo } from 'react';
import { formatDashedYYYYMMDD, parseDate as parseDateFromString } from 'utils';
import { BlueprintCards } from '~/components/BlueprintIndex/BlueprintCards/BlueprintCards';
import { BlueprintIndexControls } from '~/components/BlueprintIndex/BlueprintIndexControls/BlueprintIndexControls';
import { BlueprintIndexSearch, type PricedBlueprint } from '~/components/BlueprintIndex/BlueprintIndexSearch/BlueprintIndexSearch';
import { Divider } from '~/components/Divider';
import { Drawer } from '~/components/Drawer';
import { FlexibleDatesPanel } from '~/components/FlexibleDatesPanel';
import { Heading } from '~/components/Heading';
import { Box } from '~/components/Layout/Box';
import { LoadingSpinner } from '~/components/LoadingSpinner/LoadingSpinner';
import { useIsDesktop } from '~/hooks/useIsDesktop';
import type { AllBlueprints } from '~/libs/dato/queries/blueprints';
import type { Countries } from '~/libs/dato/queries/countries';
import type { Places } from '~/libs/dato/queries/places';
import type { Regions } from '~/libs/dato/queries/regions';
import { addPricesToBlueprints, type BpiFilter, sortBlueprintsDefault, useBlueprintIndexStore } from '~/stores/blueprintIndexStore';
import { usePlaygroundActions, usePlaygroundUIState } from '~/stores/playgroundStore';
import { buildDestinationNameMaps } from '~/utils/blueprintHelpers/destinationLabels';
import styles from './index.module.css';
export type DestinationNamesData = {
  localised: {
    countries: Countries['allCountries'];
    places: Places['allPlaces'];
    regions: Regions['allRegions'];
  };
  english: {
    countries: Countries['allCountries'];
    places: Places['allPlaces'];
    regions: Regions['allRegions'];
  };
};
const DynamicBreadcrumbs = dynamic(() => import('~/components/Breadcrumbs').then(mod => mod.Breadcrumbs), {
  ssr: false
});
const CONVERT_PARAMS_TO_FILTER_VALUES = {
  'Sleeper train': 'jai_sleeper_train',
  'Long weekend': 'long_weekend'
} as const;
type BlueprintPrices = {
  startDate: string;
  journeyUniqueId: string;
  totalPrepayAmount: number;
  totalPrepayAmountUS: number;
  totalPrepayAmountCurrency: string;
  concatenatedStatus: string | null;
}[];
interface BlueprintIndexClientProps {
  blueprintPrices: BlueprintPrices;
  cmsBlueprints: AllBlueprints;
  startDate?: string | null;
  destination: string;
  destinationNames: DestinationNamesData;
  loyaltyDiscount?: number;
  loyaltyTierId?: string;
  loyaltyTierName?: string;
}
export default function BlueprintIndexClient(t0) {
  const $ = _c(86);
  const {
    blueprintPrices,
    startDate,
    cmsBlueprints,
    destination,
    destinationNames,
    loyaltyDiscount: t1,
    loyaltyTierId,
    loyaltyTierName
  } = t0;
  const loyaltyDiscount = t1 === undefined ? 0 : t1;
  const {
    setPricedBlueprints,
    setStartDate,
    setDestinations,
    setLoading,
    loading,
    setFilters
  } = useBlueprintIndexStore(_temp);
  let t2;
  if ($[0] !== destinationNames.english || $[1] !== destinationNames.localised) {
    t2 = buildDestinationNameMaps(destinationNames.localised, destinationNames.english);
    $[0] = destinationNames.english;
    $[1] = destinationNames.localised;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const destinationNameMaps = t2;
  const t = useTranslations();
  const searchParams = useSearchParams();
  let t3;
  if ($[3] !== searchParams) {
    t3 = searchParams?.get("themes");
    $[3] = searchParams;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const themes = t3;
  let t4;
  if ($[5] !== searchParams) {
    t4 = searchParams?.get("duration");
    $[5] = searchParams;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const duration = t4;
  const isDesktop = useIsDesktop();
  const {
    isDrawerOpen,
    drawerItem
  } = usePlaygroundUIState();
  const {
    closeDrawer
  } = usePlaygroundActions();
  let t5;
  if ($[7] !== destination || $[8] !== setDestinations) {
    t5 = () => {
      setDestinations(destination ? new Set([destination]) : new Set());
    };
    $[7] = destination;
    $[8] = setDestinations;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== destination) {
    t6 = [destination];
    $[10] = destination;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  useEffect(t5, t6);
  let t7;
  if ($[12] !== setStartDate || $[13] !== startDate) {
    t7 = () => {
      if (startDate) {
        setStartDate(formatDashedYYYYMMDD(parseDateFromString(startDate)));
      } else {
        setStartDate(null);
      }
    };
    $[12] = setStartDate;
    $[13] = startDate;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== startDate) {
    t8 = [startDate];
    $[15] = startDate;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  useEffect(t7, t8);
  let t9;
  if ($[17] !== blueprintPrices || $[18] !== cmsBlueprints || $[19] !== setLoading || $[20] !== setPricedBlueprints) {
    t9 = () => {
      const pricedBlueprints = addPricesToBlueprints(blueprintPrices, cmsBlueprints) as PricedBlueprint[];
      const sortedPricedBlueprints = sortBlueprintsDefault(pricedBlueprints);
      setPricedBlueprints(sortedPricedBlueprints);
      setLoading(false);
    };
    $[17] = blueprintPrices;
    $[18] = cmsBlueprints;
    $[19] = setLoading;
    $[20] = setPricedBlueprints;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== blueprintPrices || $[23] !== cmsBlueprints) {
    t10 = [blueprintPrices, cmsBlueprints];
    $[22] = blueprintPrices;
    $[23] = cmsBlueprints;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  useEffect(t9, t10);
  let t11;
  if ($[25] !== duration || $[26] !== setFilters || $[27] !== themes) {
    t11 = () => {
      const filters = [];
      if (themes) {
        const convertedToJaiAttribute = CONVERT_PARAMS_TO_FILTER_VALUES?.[themes] as string | undefined;
        filters.push(convertedToJaiAttribute as BpiFilter);
      }
      if (duration) {
        const convertedToJaiAttribute_0 = CONVERT_PARAMS_TO_FILTER_VALUES?.[duration] as string | undefined;
        filters.push(convertedToJaiAttribute_0 as BpiFilter);
      }
      setFilters(filters);
    };
    $[25] = duration;
    $[26] = setFilters;
    $[27] = themes;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== duration || $[30] !== themes) {
    t12 = [themes, duration];
    $[29] = duration;
    $[30] = themes;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  useEffect(t11, t12);
  let t13;
  if ($[32] !== t) {
    t13 = t("navigation.home");
    $[32] = t;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== t13) {
    t14 = {
      label: t13,
      href: "/"
    };
    $[34] = t13;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== t) {
    t15 = t("navigation.search");
    $[36] = t;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== t15) {
    t16 = {
      label: t15,
      href: "/blueprint-index",
      isCurrent: true
    };
    $[38] = t15;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== t14 || $[41] !== t16) {
    t17 = <DynamicBreadcrumbs items={[t14, t16]} />;
    $[40] = t14;
    $[41] = t16;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== t) {
    t18 = t("content.blueprintIndex.index_title");
    $[43] = t;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== t18) {
    t19 = <Heading level="h1" variant="heading-lg">{t18}</Heading>;
    $[45] = t18;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== t) {
    t20 = t("content.blueprintIndex.seo_description");
    $[47] = t;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  let t21;
  if ($[49] !== t20) {
    t21 = <p className="mb-6 text-body-2 text-text-body">{t20}</p>;
    $[49] = t20;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== t19 || $[52] !== t21) {
    t22 = <div className="sr-only">{t19}{t21}</div>;
    $[51] = t19;
    $[52] = t21;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  let t23;
  if ($[54] !== cmsBlueprints || $[55] !== destinationNameMaps || $[56] !== startDate) {
    t23 = <BlueprintIndexSearch cmsBlueprints={cmsBlueprints} startDate={startDate} destinationNameMaps={destinationNameMaps} />;
    $[54] = cmsBlueprints;
    $[55] = destinationNameMaps;
    $[56] = startDate;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  let t24;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <div className={styles.divider}><Divider variant="horizontal" /></div>;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  let t25;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <BlueprintIndexControls />;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  let t26;
  if ($[60] !== loading) {
    t26 = loading && <LoadingSpinner />;
    $[60] = loading;
    $[61] = t26;
  } else {
    t26 = $[61];
  }
  let t27;
  if ($[62] !== loading) {
    t27 = clsx({
      [styles.blueprintCards]: true,
      [styles.loading]: loading
    });
    $[62] = loading;
    $[63] = t27;
  } else {
    t27 = $[63];
  }
  let t28;
  if ($[64] !== loyaltyDiscount || $[65] !== loyaltyTierId || $[66] !== loyaltyTierName) {
    t28 = <BlueprintCards loyaltyDiscount={loyaltyDiscount} loyaltyTierId={loyaltyTierId} loyaltyTierName={loyaltyTierName} />;
    $[64] = loyaltyDiscount;
    $[65] = loyaltyTierId;
    $[66] = loyaltyTierName;
    $[67] = t28;
  } else {
    t28 = $[67];
  }
  let t29;
  if ($[68] !== t27 || $[69] !== t28) {
    t29 = <div className={t27}>{t28}</div>;
    $[68] = t27;
    $[69] = t28;
    $[70] = t29;
  } else {
    t29 = $[70];
  }
  let t30;
  if ($[71] !== t26 || $[72] !== t29) {
    t30 = <div className={styles.mainContent}>{t25}<div className="relative w-full">{t26}{t29}</div></div>;
    $[71] = t26;
    $[72] = t29;
    $[73] = t30;
  } else {
    t30 = $[73];
  }
  let t31;
  if ($[74] !== closeDrawer || $[75] !== drawerItem || $[76] !== isDesktop || $[77] !== isDrawerOpen || $[78] !== t) {
    t31 = !isDesktop && <Drawer isOpen={isDrawerOpen && drawerItem === "dates"} close={closeDrawer} ariaLabel={t("content.blueprintIndex.dates_aria")} maxWidth="442px" noHeader={true}><FlexibleDatesPanel layout="vertical" /></Drawer>;
    $[74] = closeDrawer;
    $[75] = drawerItem;
    $[76] = isDesktop;
    $[77] = isDrawerOpen;
    $[78] = t;
    $[79] = t31;
  } else {
    t31 = $[79];
  }
  let t32;
  if ($[80] !== t17 || $[81] !== t22 || $[82] !== t23 || $[83] !== t30 || $[84] !== t31) {
    t32 = <Box space="--by-space-400">{t17}{t22}{t23}{t24}{t30}{t31}</Box>;
    $[80] = t17;
    $[81] = t22;
    $[82] = t23;
    $[83] = t30;
    $[84] = t31;
    $[85] = t32;
  } else {
    t32 = $[85];
  }
  return t32;
}
function _temp(state) {
  return state;
}